import {
  AdvancedTargetingSpec,
  FacebookTargetingSpec,
  Gender,
  SavedAudience,
} from '@marketing-milk/interfaces'
import { displayAudienceSize } from '@marketing-milk/frontend'

/**
 * Read-only presentation helpers over a SavedAudience's targeting spec, for the
 * packet Audience section. These deliberately avoid the operational audience
 * builder state — they only flatten the stored spec into client-presentable
 * strings/tags. Full targeting detail is still available via the reused
 * AudienceBreakdown deep-dive.
 */

// Fields on a (flexible) targeting spec that hold named interest/behavior/etc
// rows we want to surface as tags.
const TAG_FIELDS: readonly (keyof AdvancedTargetingSpec)[] = [
  'interests',
  'behaviors',
  'life_events',
  'industries',
  'income',
  'family_statuses',
  'education_majors',
  'work_employers',
  'work_positions',
]

function namesFrom(rows: unknown): string[] {
  if (!Array.isArray(rows)) return []
  return rows
    .map(r => (r && typeof r === 'object' ? (r as { name?: string }).name : undefined))
    .filter((n): n is string => typeof n === 'string' && n.length > 0)
}

/**
 * Flatten the named interest/persona/behavior rows from a targeting spec into a
 * deduped tag list, reaching into `flexible_spec` (the OR-groups Meta uses) as
 * well as the top-level fields.
 */
export function audienceTags(targeting?: FacebookTargetingSpec): string[] {
  if (!targeting) return []
  const specs: AdvancedTargetingSpec[] = [targeting, ...(targeting.flexible_spec ?? [])]
  const tags = specs.flatMap(spec => TAG_FIELDS.flatMap(field => namesFrom(spec[field])))
  return Array.from(new Set(tags))
}

function genderLabel(genders?: Gender[]): string {
  if (!genders || genders.length === 0 || genders.length === 2 || genders[0] === Gender.All) {
    return 'All genders'
  }
  return genders[0] === Gender.Male ? 'Men' : 'Women'
}

function locationLabel(targeting?: FacebookTargetingSpec): string {
  const geo = targeting?.geo_locations
  if (!geo) return 'Location not set'
  const parts: string[] = []
  parts.push(...namesFrom(geo.cities))
  parts.push(...namesFrom(geo.regions))
  parts.push(...((geo.countries ?? []) as string[]))
  // custom_locations may be present with a radius; surface the name when set.
  parts.push(...namesFrom(geo.custom_locations))
  if (parts.length === 0) return 'Location not set'
  const shown = parts.slice(0, 3).join(', ')
  return parts.length > 3 ? `${shown} +${parts.length - 3} more` : shown
}

export interface AudienceSummary {
  age: string
  genders: string
  location: string
}

export function audienceSummary(audience: SavedAudience): AudienceSummary {
  const t = audience.targeting
  return {
    age: t ? `Ages ${t.age_min}–${t.age_max}` : 'Age not set',
    genders: genderLabel(t?.genders),
    location: locationLabel(t),
  }
}

/** Clean, read-only size range (no recalc affordance) for cards + Present. */
export function audienceSizeText(audience: SavedAudience): string {
  return displayAudienceSize(
    Number(audience.approximateCountLowerBound),
    Number(audience.approximateCountUpperBound)
  )
}
